<script lang="ts" setup>
import {ExtendedProject, GroupBy, Upload} from "@/types";
import ProjectLayout from "@/Pages/projects/layouts/ProjectLayout.vue";
import BaseLayout from "@/layouts/BaseLayout.vue";
import UploadsHistory from "@/Pages/projects/components/UploadsHistory.vue";
import NoTransferCard from "@/Pages/projects/users/components/NoTransferCard.vue";
import {CalendarHeatmap, Value} from "vue3-calendar-heatmap";
import {Head, router} from "@inertiajs/vue3";
import {route} from "ziggy-js";

interface Props {
    project: ExtendedProject,
    uploads: GroupBy<Upload>,
    contributions: Array<Value>,
    totalContributions: number,
    withDescription?: boolean,
}

const props = withDefaults(defineProps<Props>(), {
    withDescription: false,
})
defineOptions({
    layout: BaseLayout
})

const date = new Date().toISOString().slice(0, 10)

const filterByDate = (event: { date: Date; count: number; colorIndex: number }) => {
    const inputDate = new Date(event.date.getTime() - (event.date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10)

    const url = new URL(window.location.href)
    const date = url.searchParams.get('date')

    if (date === inputDate) {
        router.visit(
            route('projects.show', {project: props.project.slug})
        )
        return
    }

    router.visit(
        route('projects.show', {
            project: props.project.slug, _query: {
                date: inputDate
            }
        })
    )
}
</script>

<template>
    <Head :title="props.project.name" />
    <project-layout
        :project="props.project"
        :with-description="props.withDescription"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            {{ props.project.name }}
            <v-chip
                variant="outlined"
                size="x-small"
                class="text-caption font-weight-bold px-2 ml-1 mb-1"
                color="grey-lighten-1"
            >
                <span class="text-grey-darken-1">{{ props.project.is_public ? 'Public' : 'Private' }}</span>
            </v-chip>
            <v-menu
                :close-on-content-click="false"
                location="bottom"
                :eager="true"
            >
                <template #activator="{ props: attrs }">
                    <success-btn
                        v-bind="attrs"
                        class="float-end"
                        position="relative"
                        rounded="lg"
                        border="sm"
                        append-icon="mdi-chevron-down"
                    >
                        <span class="text-body-2 font-weight-bold">Configuration</span>
                    </success-btn>
                </template>
                <v-card
                    min-width="192"
                    rounded="lg"
                    class="mt-1"
                >
                    <v-list
                        density="comfortable"
                        :slim="true"
                        class="pa-1"
                    >
                        <v-list-item
                            density="compact"
                            :slim="true"
                            prepend-icon="mdi-zip-box-outline"
                            :link="true"
                            rounded="lg"
                            :href="route('projects.configs.create', {project: props.project.slug})"
                        >
                            Download ZIP
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-menu>
        </h1>
        <v-divider class="my-5" />
        <no-transfer-card
            v-if="props.totalContributions === 0"
        />
        <div v-if="props.totalContributions > 0">
            <v-card
                min-height="150"
                min-width="100%"
                variant="flat"
                class="pa-3 mt-2 mb-10"
                rounded="lg"
                border="sm"
            >
                <calendar-heatmap
                    :end-date="date"
                    :values="contributions"
                    tooltip-unit="transfers"
                    :round="2"
                    @day-click="filterByDate($event)"
                />
            </v-card>
            <uploads-history
                :uploads="props.uploads"
            />
        </div>
    </project-layout>
</template>

<style scoped>

</style>
